import React, { useEffect, useState } from "react";
import { Menu, X, Instagram } from "lucide-react";
import { NAV_LINKS, PROFILE } from "@/lib/portfolioData";

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("home");

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
      // Determine active section
      const sections = NAV_LINKS.map((l) => document.getElementById(l.id)).filter(Boolean);
      const y = window.scrollY + window.innerHeight / 3;
      for (let i = sections.length - 1; i >= 0; i--) {
        if (sections[i].offsetTop <= y) {
          setActive(sections[i].id);
          break;
        }
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <header
        data-testid="site-nav"
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
          scrolled
            ? "backdrop-blur-xl bg-[#0A0A0A]/75 border-b border-white/5 py-3"
            : "bg-transparent py-6"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex items-center justify-between">
          <button
            onClick={() => scrollTo("home")}
            className="flex items-center gap-2 group"
            data-testid="nav-logo"
          >
            <div className="w-9 h-9 rounded-full border border-[#D4AF37]/40 flex items-center justify-center font-display font-bold text-[#D4AF37] group-hover:bg-[#D4AF37] group-hover:text-black transition-all duration-500">
              TK
            </div>
            <span className="hidden sm:inline font-display text-sm tracking-[0.25em] text-white/80 uppercase">
              {PROFILE.name}
            </span>
          </button>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollTo(link.id)}
                data-testid={`nav-link-${link.id}`}
                className={`link-hover text-xs uppercase tracking-[0.2em] font-medium font-display transition-colors duration-300 ${
                  active === link.id ? "text-[#D4AF37]" : "text-white/70 hover:text-white"
                }`}
              >
                {link.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={PROFILE.instagramUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="nav-instagram"
              className="hidden sm:flex w-9 h-9 rounded-full border border-white/10 items-center justify-center text-white/70 hover:text-[#D4AF37] hover:border-[#D4AF37]/50 transition-all"
              aria-label="Instagram"
            >
              <Instagram className="w-4 h-4" />
            </a>
            <button
              onClick={() => scrollTo("contact")}
              data-testid="nav-cta"
              className="hidden md:inline-flex magnetic-btn"
            >
              Let's Talk
            </button>
            <button
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden w-10 h-10 flex items-center justify-center text-white"
              data-testid="nav-menu-toggle"
              aria-label="Open menu"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      {open && (
        <div
          className="fixed inset-0 z-30 bg-[#0A0A0A]/98 backdrop-blur-xl pt-24 px-6 lg:hidden"
          data-testid="mobile-menu"
        >
          <nav className="flex flex-col gap-6">
            {NAV_LINKS.map((link, i) => (
              <button
                key={link.id}
                onClick={() => scrollTo(link.id)}
                data-testid={`mobile-nav-${link.id}`}
                className="text-left font-display text-3xl uppercase tracking-tight text-white/90 hover:text-[#D4AF37] transition-colors"
              >
                <span className="text-[#D4AF37]/40 text-sm mr-3">0{i + 1}</span>
                {link.label}
              </button>
            ))}
            <a
              href={PROFILE.instagramUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-8 text-[#B3B3B3] text-sm flex items-center gap-2"
            >
              <Instagram className="w-4 h-4" /> @{PROFILE.instagram}
            </a>
          </nav>
        </div>
      )}
    </>
  );
}
